import { ClickableRegion, CONSTANTS } from './types';

// Tooltip region interface
interface TooltipRegion {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  text: string | string[]; // Single line or multiple lines
}

// Tooltip display constants
const TOOLTIP_DELAY_MS = 500; // 500ms delay before showing tooltip
const TOOLTIP_PADDING = 8;
const TOOLTIP_FONT = '12px "comic sans ms"';
const TOOLTIP_LINE_HEIGHT = 16;

// Helper to lighten a hex color for hover effect
function lightenColor(hex: string, amount: number = 30): string {
  // Handle rgb() format
  if (hex.startsWith('rgb')) {
    const match = hex.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      const r = Math.min(255, parseInt(match[1]!, 10) + amount);
      const g = Math.min(255, parseInt(match[2]!, 10) + amount);
      const b = Math.min(255, parseInt(match[3]!, 10) + amount);
      return `rgb(${r}, ${g}, ${b})`;
    }
  }

  // Handle hex format
  let color = hex.replace('#', '');
  if (color.length === 3) {
    color = color[0]! + color[0]! + color[1]! + color[1]! + color[2]! + color[2]!;
  }

  const r = Math.min(255, parseInt(color.substring(0, 2), 16) + amount);
  const g = Math.min(255, parseInt(color.substring(2, 4), 16) + amount);
  const b = Math.min(255, parseInt(color.substring(4, 6), 16) + amount);

  return `rgb(${r}, ${g}, ${b})`;
}

export class Canvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private clickableRegions: ClickableRegion[] = [];
  private tooltipRegions: TooltipRegion[] = [];
  private mouseX: number = -1;
  private mouseY: number = -1;

  // Tooltip hover tracking
  private hoveredTooltipId: string | null = null;
  private hoverStartTime: number = 0;

  constructor(canvasElement: HTMLCanvasElement) {
    this.canvas = canvasElement;

    const ctx = canvasElement.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    this.ctx = ctx;

    this.updateCanvasSize();
    window.addEventListener('resize', () => this.updateCanvasSize());

    // Track mouse position for hover states (in logical coordinates)
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const scaleX = CONSTANTS.CANVAS_WIDTH / rect.width;
      const scaleY = CONSTANTS.CANVAS_HEIGHT / rect.height;
      this.mouseX = (e.clientX - rect.left) * scaleX;
      this.mouseY = (e.clientY - rect.top) * scaleY;
    });

    this.canvas.addEventListener('mouseleave', () => {
      this.mouseX = -1;
      this.mouseY = -1;
      this.hoveredTooltipId = null;
    });
  }

  private updateCanvasSize(): void {
    const dpr = window.devicePixelRatio || 1;
    const aspectRatio = CONSTANTS.CANVAS_WIDTH / CONSTANTS.CANVAS_HEIGHT;

    // Fill viewport while maintaining aspect ratio
    let displayWidth: number;
    let displayHeight: number;

    if (window.innerWidth / window.innerHeight > aspectRatio) {
      displayHeight = window.innerHeight;
      displayWidth = Math.round(displayHeight * aspectRatio);
    } else {
      displayWidth = window.innerWidth;
      displayHeight = Math.round(displayWidth / aspectRatio);
    }

    this.canvas.style.width = `${displayWidth}px`;
    this.canvas.style.height = `${displayHeight}px`;

    // Backing store matches physical pixels for crisp rendering
    this.canvas.width = Math.round(displayWidth * dpr);
    this.canvas.height = Math.round(displayHeight * dpr);

    // Scale context so drawing code uses 1024x700 logical coordinates
    const scaleX = this.canvas.width / CONSTANTS.CANVAS_WIDTH;
    const scaleY = this.canvas.height / CONSTANTS.CANVAS_HEIGHT;
    this.ctx.setTransform(scaleX, 0, 0, scaleY, 0, 0);
  }

  clear(color: string = CONSTANTS.MAIN_BACKGROUND): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, CONSTANTS.CANVAS_WIDTH, CONSTANTS.CANVAS_HEIGHT);
    this.clickableRegions = [];
    this.tooltipRegions = [];
  }

  drawImage(
    source: HTMLImageElement | HTMLCanvasElement,
    x: number,
    y: number,
    width?: number,
    height?: number
  ): void {
    if (width !== undefined && height !== undefined) {
      this.ctx.drawImage(source, x, y, width, height);
    } else {
      this.ctx.drawImage(source, 0, 0, source.width, source.height, x, y, source.width, source.height);
    }
  }

  drawRect(x: number, y: number, width: number, height: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x, y, width, height);
  }

  drawRectOutline(
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    lineWidth: number = 1
  ): void {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;
    this.ctx.strokeRect(x, y, width, height);
  }

  drawText(
    text: string,
    x: number,
    y: number,
    font: string,
    color: string,
    align: CanvasTextAlign = 'left'
  ): void {
    this.ctx.font = font;
    this.ctx.fillStyle = color;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = 'top';
    this.ctx.fillText(text, x, y);
  }

  drawOutlinedText(
    text: string,
    x: number,
    y: number,
    font: string,
    fillColor: string,
    outlineColor: string,
    outlineWidth: number = 2,
    align: CanvasTextAlign = 'left'
  ): void {
    this.ctx.font = font;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = 'top';

    // Draw outline
    this.ctx.strokeStyle = outlineColor;
    this.ctx.lineWidth = outlineWidth;
    this.ctx.lineJoin = 'round';
    this.ctx.strokeText(text, x, y);

    // Draw fill
    this.ctx.fillStyle = fillColor;
    this.ctx.fillText(text, x, y);
  }

  measureText(text: string, font: string): TextMetrics {
    this.ctx.font = font;
    return this.ctx.measureText(text);
  }

  addClickableRegion(region: ClickableRegion): void {
    this.clickableRegions.push(region);
  }

  clearClickableRegions(): void {
    this.clickableRegions = [];
  }

  drawButton(
    id: string,
    text: string,
    x: number,
    y: number,
    width: number,
    height: number,
    font: string,
    textColor: string,
    bgColor?: string,
    onClick?: () => void
  ): void {
    // Check if mouse is hovering over this button
    const isHovered = this.mouseX >= x &&
      this.mouseX <= x + width &&
      this.mouseY >= y &&
      this.mouseY <= y + height;

    if (bgColor) {
      const color = isHovered ? lightenColor(bgColor) : bgColor;
      this.drawRect(x, y, width, height, color);
    }

    // Center text in button
    this.ctx.font = font;
    const metrics = this.ctx.measureText(text);
    const textX = x + (width - metrics.width) / 2;
    const textY = y + (height - 16) / 2; // Approximate font height

    this.drawText(text, textX, textY, font, textColor);

    if (onClick) {
      this.addClickableRegion({
        id,
        x,
        y,
        width,
        height,
        onClick,
      });
    }
  }

  // Get current mouse position
  getMousePosition(): { x: number; y: number } {
    return { x: this.mouseX, y: this.mouseY };
  }

  // Check if a region is hovered
  isHovered(x: number, y: number, width: number, height: number): boolean {
    return this.mouseX >= x &&
      this.mouseX <= x + width &&
      this.mouseY >= y &&
      this.mouseY <= y + height;
  }

  handleClick(x: number, y: number): boolean {
    for (const region of this.clickableRegions) {
      if (
        x >= region.x &&
        x <= region.x + region.width &&
        y >= region.y &&
        y <= region.y + region.height
      ) {
        region.onClick();
        return true;
      }
    }
    return false;
  }

  getClickableRegions(): ClickableRegion[] {
    return [...this.clickableRegions];
  }

  getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  // Register a tooltip region
  addTooltip(
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
    text: string | string[]
  ): void {
    this.tooltipRegions.push({ id, x, y, width, height, text });
  }

  // Update tooltip hover state (call each frame before renderTooltip)
  updateTooltipHover(): void {
    // Find which tooltip region is hovered (if any)
    let hoveredRegion: TooltipRegion | null = null;
    for (const region of this.tooltipRegions) {
      if (
        this.mouseX >= region.x &&
        this.mouseX <= region.x + region.width &&
        this.mouseY >= region.y &&
        this.mouseY <= region.y + region.height
      ) {
        hoveredRegion = region;
        break;
      }
    }

    if (hoveredRegion) {
      if (this.hoveredTooltipId !== hoveredRegion.id) {
        // Started hovering a new region
        this.hoveredTooltipId = hoveredRegion.id;
        this.hoverStartTime = Date.now();
      }
    } else {
      this.hoveredTooltipId = null;
    }
  }

  // Render tooltip if hover delay has passed
  renderTooltip(): void {
    if (!this.hoveredTooltipId) return;

    // Check if delay has passed
    const hoverDuration = Date.now() - this.hoverStartTime;
    if (hoverDuration < TOOLTIP_DELAY_MS) return;

    // Find the hovered region
    const region = this.tooltipRegions.find(r => r.id === this.hoveredTooltipId);
    if (!region) return;

    const lines = Array.isArray(region.text) ? region.text : [region.text];

    // Calculate tooltip dimensions
    this.ctx.font = TOOLTIP_FONT;
    let maxWidth = 0;
    for (const line of lines) {
      const metrics = this.ctx.measureText(line);
      maxWidth = Math.max(maxWidth, metrics.width);
    }

    const tooltipWidth = maxWidth + TOOLTIP_PADDING * 2;
    const tooltipHeight = lines.length * TOOLTIP_LINE_HEIGHT + TOOLTIP_PADDING * 2;

    // Position tooltip near mouse, but keep on screen
    let tooltipX = this.mouseX + 15;
    let tooltipY = this.mouseY + 15;

    // Keep tooltip on screen
    if (tooltipX + tooltipWidth > CONSTANTS.CANVAS_WIDTH) {
      tooltipX = this.mouseX - tooltipWidth - 5;
    }
    if (tooltipY + tooltipHeight > CONSTANTS.CANVAS_HEIGHT) {
      tooltipY = this.mouseY - tooltipHeight - 5;
    }

    // Draw tooltip background
    this.ctx.fillStyle = '#222222';
    this.ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);
    this.ctx.strokeStyle = '#666666';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Draw tooltip text
    this.ctx.fillStyle = '#ffffff';
    this.ctx.font = TOOLTIP_FONT;
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line) {
        this.ctx.fillText(
          line,
          tooltipX + TOOLTIP_PADDING,
          tooltipY + TOOLTIP_PADDING + i * TOOLTIP_LINE_HEIGHT
        );
      }
    }
  }
}
